import {
  ArticleData,
  ArticleResource,
  Matching,
  MeasurementParams,
  MeasurementProcess,
  MultiRecommendation,
  ProductServiceInterface,
  ShopSettings,
  FLOW_END_MAP,
  CONVERSION_ACTION,
  SHOP_PLATFORM,
  TAGS_QUERY_TYPE,
} from '../interfaces/ProductService.interface';
import RenderUtil from '../util/RenderUtil';
import articleService from './ArticleService';
import sessionService from './SessionService';
import storageService from './StorageService';
import conversionsService from './ConversionsService';
import matchingService from './MatchingService';
import recommendationService from './RecommendationService';
import shopSettingsService from './ShopSettingsService';
import { PollingState } from '../components/QRGateway';
import { DefaultError } from '../error/DefaultError';
import clientEventsService from './ClientEventsService';
import { ShopifyHelper } from './ShopifyHelper';
import log, { LogMode } from './LogService';
import measurementService from './MeasurementService';


let customShopService: ProductServiceInterface;

function hasArticle(data) {
  return data && data.length > 0;
}

function getSessionLastMeasurement() {
  return sessionService.getSessionMeasurements()
    .then(result => {
      if (result && result.length > 0) {
        return result[0];
      }
      return null;
    });
}

function checkMeasurementState() {
  return sessionService.getSessionEvents()
    .then(events => {
      // Check if session CreatedAt is older than 30 minutes

      const session = events.pop();

      const createdAt = new Date(session.createdAt);
      const now = new Date();
      const diff = now.getTime() - createdAt.getTime();
      const minutes = Math.floor(diff / 1000 / 60);

      if (minutes > 5) {
        log.info('polling session expired');
        return PollingState.TIMEOUT;
      }

      if (events.length === 0 ||
         (events.length > 0 && events[0].event_name === 'shop.qr.displayed')) {
        return PollingState.UNDEFINED;
      }

      const initialValue = PollingState.MEASURING;

      return events.reduce(
        (accumulator: PollingState, currentValue) => {
          if (accumulator === PollingState.DONE ||
              accumulator === PollingState.CANCELLED) {
            return accumulator;
          }

          if (currentValue.event_name === 'con.measurement.success') {
            return PollingState.DONE;
          }

          if (currentValue.event_name === 'shop.qr.closed') {
            return PollingState.CANCELLED;
          }

          return accumulator;

        }, initialValue,
      );

    });
}

function waitForPollingOutcome(expected: PollingState) {
  return new Promise((fulfill, reject) => {

    let toId: string | number | NodeJS.Timeout;

    const poll = () => {
      log.info('polling . . .');
      checkMeasurementState()
        .then((outcome:PollingState) => {

          log.info('Polling: ', outcome);

          // setPollingState(outcome);
          const isFinalOutcome =
            outcome === PollingState.DONE ||
            outcome === PollingState.CANCELLED ||
            outcome === PollingState.TIMEOUT;

          const continuePolling = !isFinalOutcome;

          if (outcome === expected) {
            log.info('stopped polling');
            clearTimeout(toId);
            fulfill(outcome);
          }

          if (continuePolling) {
            log.info('waiting for user measurement');
            toId = setTimeout(poll, 1000);
          } else if (outcome === PollingState.DONE) {
            log.info('stopped polling');
            clearTimeout(toId);
            fulfill(outcome);
          } else {
            log.info('stopped polling');
            clearTimeout(toId);
            reject(outcome);
          }
        })
        .catch((err) => {
          reject(err);
        });
    }

    log.info('start polling');
    poll();
  });
}

let shopSettings: ShopSettings = null;
let loginReference: string = null;

const ShopService =  {

  // FIXME: Temporary support for legacy custom service
  setCustomService(service: ProductServiceInterface) {
    customShopService = service;
  },

  setApiKey(apiKey:string) {
    sessionService.setApiKey(apiKey);
    conversionsService.setApiKey(apiKey);
    shopSettingsService.setApiKey(apiKey);
  },

  setShopSettings(settings:ShopSettings) {
    shopSettings = settings;
  },

  setLoginReference(ref:string) {
    loginReference = ref;
  },

  hasShopSetting() {
    return !!shopSettings;
  },

  getArticleData(fptEl:HTMLElement): ArticleData {

    const article: ArticleData = {
      id: '',
      gtin: '',
      ean: '',
      sku: '',
      number: '',
      sizes: [],
    };

    log.info('getting article data', fptEl.dataset);

    if ('articleNumber' in fptEl.dataset) {
      log.info('got article number', fptEl.dataset.articleNumber);
      article.number = fptEl.dataset.articleNumber;
      article.id = article.number;
    } else if ('gtin' in fptEl.dataset) {
      log.info('got article gtin', fptEl.dataset.gtin);
      article.gtin = fptEl.dataset.gtin;
    } else if ('ean' in fptEl.dataset) {
      log.info('got article ean', fptEl.dataset.ean);
      article.ean = fptEl.dataset.ean;
    } else if ('sku' in fptEl.dataset) {
      log.info('got article sku', fptEl.dataset.sku);
      article.sku = fptEl.dataset.sku;
    } else if (customShopService) {
      return customShopService.getArticleData();
    }

    if ('articleName' in fptEl.dataset) {
      log.info('got article name', fptEl.dataset.articleName);
      article.name = fptEl.dataset.articleName;
    }

    if ('articleImage' in fptEl.dataset) {
      log.info('got article image', fptEl.dataset.articleImage);
      article.image = fptEl.dataset.articleImage;
    }

    if ('articlePrice' in fptEl.dataset) {
      log.info('got article price', fptEl.dataset.articlePrice);
      const str = fptEl.dataset.articlePrice;
      const price = Number(str);
      article.price = Number.isNaN(price) || str === '' ? null : price;
      log.info(`parsed article price ${article.price}`);
    }

    if ('currency' in fptEl.dataset) {
      log.info('got currency', fptEl.dataset.currency);
      article.currency = fptEl.dataset.currency;
    }

    if ('returnUrl' in fptEl.dataset) {
      log.info('got return URL', fptEl.dataset.returnUrl);
      article.returnUrl = fptEl.dataset.returnUrl;
    }

    if ('mode' in fptEl.dataset) {
      log.info('got return URL', fptEl.dataset.mode);
      article.mode = fptEl.dataset.mode;
    }

    if (customShopService) {
      const customArticle = customShopService.getArticleData();
      article.sizes = customArticle.sizes || [];
    }

    return article;
  },

  getApiKey(config?:any): string {

    if (config.apiKey) {
      return config.apiKey;
    }

    const fptEl = document.querySelector('#footprinttech-button') as HTMLElement;

    if (fptEl && 'apiKey' in fptEl.dataset) {
      log.info('got API key', fptEl.dataset.apiKey);
      return fptEl.dataset.apiKey;
    } else if (customShopService) {
      return customShopService.getApiKey();
    }

    return '';
  },

  getClientSessionId(): string {

    const fptEl = RenderUtil.getFPTTag();

    if (fptEl && 'sessionId' in fptEl.dataset) {
      log.info('got client session id', fptEl.dataset.sessionId);
      return fptEl.dataset.sessionId;
    } else if (customShopService) {
      return customShopService.getClientSessionId();
    }

    return '';
  },

  parseReturnUrl(baseUrl:string, measurementData: any, article: ArticleData, customerReference?: string): string {
    const { sessionId, measurementId } = measurementData;
    const { number } = article;
    let url = `${baseUrl}?fpt-measurement=${measurementId}&fpt-session=${sessionId}`;
    url = number ? `${url}&fpt-article-number=${number}` : url;
    if (customerReference) {
      url = `${url}&fpt-customer-reference=${customerReference}`;
    }
    return url;
  },

  parseReturnUrlWithRecommendation(baseUrl:string, matching:Matching, additionalParams?:string): string {
    const measurementId = matching.measurement_id;
    const sessionId = sessionService.getSessionId();
    const articleNumber = matching.article_number;
    const lengthRight = matching.length_right;
    const lengthLeft = matching.length_left;
    const widthRight = matching.width_right;
    const widthLeft = matching.width_left;
    const halluxRight = matching.hallux_right;
    const halluxLeft = matching.hallux_left;
    const size = matching.size ? matching.size.value : 'NA';

    let url = `${baseUrl}?fpt-measurement=${measurementId}&fpt-session=${sessionId}&fpt-lengthr=${lengthRight}&fpt-lengthl=${lengthLeft}&fpt-widthr=${widthRight}&fpt-widthl=${widthLeft}&fpt-size=${size}`;

    if (halluxRight || halluxLeft) {
      url += `&fpt-halluxl=${halluxLeft}&fpt-halluxr=${halluxRight}`;
    }

    url = articleNumber ? `${url}&fpt-article-number=${articleNumber}` : url;
    url = additionalParams ? `${url}&${additionalParams}` : url;
    return url;
  },

  updateSessionId(sessionId: string) {
    storageService.update('sessionId', sessionId);
    articleService.setSessionId(sessionId);
    sessionService.setSessionId(sessionId);
    clientEventsService.setSessionId(sessionId);
    matchingService.setSessionId(sessionId);
    measurementService.setSessionId(sessionId);
    return sessionId;
  },

  createSession(): Promise<any> {
    const anonymousId = storageService.getAnonymousId();

    const sessionData = {
      screen_height: window.innerHeight,
      screen_width: window.innerWidth,
      analytics_accepted: true,
      client_session_id: this.getClientSessionId(),
      anonymous_id: anonymousId,
      referrer: window.location.href,
    };

    return sessionService.createSession(sessionData);
  },

  getTarget(): string {
    return process.env.FPT_APP_URL;
  },

  handleCtaButtonClick(article:ArticleData): Promise<any> {

    storageService.addViewedTooltip('drv.cta-button.tooltip.text');
    return this.createSession()
      .then(result => {
        this.updateSessionId(result);
        return this.checkIfArticleExists(article);
      });
  },

  handleExistingSession(sessionId:string, article:ArticleData): Promise<any> {
    storageService.addViewedTooltip('drv.cta-button.tooltip.text');
    this.updateSessionId(sessionId);
    return this.checkIfArticleExists(article);
  },

  checkIfArticleExists(article:ArticleData): Promise<ArticleResource> {
    log.info('checking if article exists');

    const articleData = {
      articleNumber: article.number,
      articleName: article.name,
      gtin: article.gtin || article.ean,
      sku: article.sku,
    };

    return articleService
      .lookupArticle(articleData)
      .then((result) => {
        const { data } = result;

        log.info('lookup article response', result);

        if (hasArticle(data)) {
          return data[0];
        }

        throw new DefaultError('WDAR1', 'Article not found');
      });
  },

  getQRData() {
    log.info('displaying desktop widget with settings', shopSettings);
    const { funnel, lang, theme, steps, desktopFlowEnd, languageSelectEnabled } = shopSettings;
    const themeName = theme ? theme.name : 'default';
    const selectedFunnel = funnel || 'UNDEFINED';
    const intermediateSteps = steps || 'DEFAULT';
    const end = desktopFlowEnd && FLOW_END_MAP[desktopFlowEnd] ? FLOW_END_MAP[desktopFlowEnd] : 'PDP';
    log.info('displaying desktop widget with end', end);

    const params: MeasurementProcess = {
      theme: themeName,
      flow: `${selectedFunnel}-${intermediateSteps}-${end}`,
      session: storageService.getSessionId(),
      end,
      gdpr: 1,
    };

    const widget = RenderUtil.getWidgetEl();

    if (lang) {
      params.lang = lang;
    }

    if (languageSelectEnabled) {
      params['lang-select'] = 1;
    }

    if (widget && ('archType' in widget.dataset)) {
      params.footArch = true;
    }

    const currentUrl = new URL(window.location.href);
    const query = new URLSearchParams(currentUrl.search);

    if (query.has('r') || query.has('ref') || query.has('referrer')) {
      params.ref = query.get('r') || query.get('ref') || query.get('referrer');
    }

    return params;

  },

  isSuccessfulMatching(m:Matching) {
    return m.error_code === 'MAST0-00';
  },

  getFptAppParams(funnel: string, article: ArticleData) {
    const { steps, lang, theme, languageSelectEnabled } = shopSettings;
    const sp = new URL(window.location.href).searchParams;
    const FlowPat = /^\w+-(.*)-(\w+)$/;
    const flowParam = sp.get('fpt-flow');

    const themeName = theme ? theme.name : 'default';
    const selectedTheme = sp.get('fpt-theme') ? sp.get('fpt-theme') : themeName;
    const flow = sp.get('fpt-flow') ? sp.get('fpt-flow') : '';
    let match = null;
    let intermediateSteps = steps || 'DEFAULT';
    let end = 'PDP';

    if (flowParam && FlowPat.test(flowParam)) {
      match = /^\w+-(.*)-(\w+)$/.exec(flowParam);
      intermediateSteps = match[1];
      end = match[2];
    }

    const params: MeasurementProcess = {
      articleNumber: article?.number,
      theme:selectedTheme,
      flow,
      steps: intermediateSteps,
      session: storageService.getSessionId(),
      funnel,
      gdpr: 1,
      end,
    };

    if (lang) {
      log.info(`[ShopService] Settings lang ${lang}`);
      params.lang = lang;
    }

    const widget = RenderUtil.getWidgetEl();

    if (languageSelectEnabled) {
      params['lang-select'] = 1;
    }

    if (widget && ('archType' in widget.dataset)) {
      params.footArch = true;
    }

    return params;
  },

  openFptAppForFunnel(funnel: string, article: ArticleData) {
    log.info(`handling guest with funnel ${funnel}`);
    const target = this.getTarget();
    const params = this.getFptAppParams(funnel, article);

    return this.openFptApp(target, params);
  },

  handleFunnelSelect(funnel: string, article: ArticleData) {
    return this.openFptAppForFunnel(funnel, article);
  },

  handleFunnelSelectEmbeddedMode() {
    const target = window.location.origin;
    // const meaFrame: HTMLElement = RenderUtil.getMeaFrame();
    return new Promise((fulfill, reject) => {
      window.addEventListener(
        "message",
        (event) => {
          log.info('got post message', event);
          const senderUrl = new URL(event.origin);
          const expectedUrl = new URL(target);
          // Do we trust the sender of this message? (might be
          // different from what we originally opened, for example).
          if (senderUrl.hostname !== expectedUrl.hostname) return;
          fulfill(event.data);
        },
        false,
      );
      // meaFrame.setAttribute('src', url.toString());
    });
  },

  getFptAppUrl(funnel: string, article: ArticleData) {
    log.info(`handling guest with funnel ${funnel}`);
    const target = this.getTarget();
    const params = this.getFptAppParams(funnel, article);

    return this.buildFptAppUrl(target, params);
  },

  processMeasurement(measurementId: string, article: ArticleData) {
    storageService.update('measurementId', measurementId);
    storageService.clearMatchings();
    return this.handleExistingMeasurement(measurementId, article);
  },

  handleMeasuringResult(data, article: ArticleData) {

    window.focus();
    log.info('received data', data);

    return new Promise((fulfill, reject) => {

      log.info('handle measuring result callback called', data.result);

      if (data.result && data.result.error && data.result.error === 'app-closed') {
        log.info('App closed by user');
        // FIXME: We need a new error code for this scenario
        reject(new DefaultError('WDCNV1', 'App closed by user'));
        return;
      }

      if (data.result && data.result.measurementId) {
        const { sessionId, measurementId } = data.result;
        this.updateSessionId(sessionId);
        const result = this.processMeasurement(measurementId, article);
        fulfill(result);
        return;
      }

      log.warn('Could not retrieve measurement data');
      // reject(new DefaultError('GEN01-00', 'Could not retrieve measurement data'));

    });
  },

  buildFptAppUrl(target: string, processData: MeasurementProcess) {
    const { funnel, steps, end } = processData;
    const intermediateSteps = steps || 'DEFAULT';
    const data:MeasurementParams = {
      theme: processData.theme,
      flow: `${funnel}-${intermediateSteps}-${end}`,
      session: storageService.getSessionId(),
      lang: processData.lang,
      gdpr: processData.gdpr,
      'lang-select': processData['lang-select'],
      footArch: processData.footArch,
    };

    if (loginReference) {
      data.ext_reference = loginReference;
    }

    return RenderUtil.getFptUrl(target, data);
  },

  openFptApp(target: string, processData: MeasurementProcess) {
    const { funnel, steps, end } = processData;
    const intermediateSteps = steps || 'DEFAULT';

    const data: MeasurementParams = {
      theme: processData.theme,
      flow: `${funnel}-${intermediateSteps}-${end}`,
      session: storageService.getSessionId(),
      lang: processData.lang,
      gdpr: processData.gdpr,
      'lang-select': processData['lang-select'],
      footArch: processData.footArch,
    };

    if (loginReference) {
      data.ext_reference = loginReference;
    }

    return RenderUtil.openFptApp(target, data);
  },

  createConversion(
    matching: Matching,
    article: ArticleData,
    action: CONVERSION_ACTION,
    reference?:string,
  ) {

    if (matching.error_code !== 'MAST0-00') {
      return;
    }

    const price = RenderUtil.parseArticlePrice(article.price, shopSettings);

    const data: any = {
      matching_id: matching.matching_id,
      price: price,
      quantity: 1,
      tax: 0,
      total: price,
      currency: article.currency,
      action,
    };

    const clientSessionId = this.getClientSessionId();

    if (clientSessionId) {
      data.client_session_id = clientSessionId;
    }

    if (reference) {
      data.customer_reference = reference;
    }

    return conversionsService.postConversion(data);
  },

  createRecommendationConversions(
    recommendation: MultiRecommendation,
    reference: string
  ) {

    const { recommendation_id } = recommendation;

    const payload = {
      session_id: recommendation.session_id,
      customer_reference: reference,
      action: CONVERSION_ACTION.MATCHING_PRIMARY,
      recommendation_id,
    };

    return conversionsService.postConversion(payload);
  },

  readClientCookie(key) {
    let cookie = document.cookie.split(';').find((item) => item.includes(key));
    if (cookie?.indexOf('=') > -1) {
      return cookie.split('=')[1];
    }
    return null;
  },

  handleMeasurementDone(article: ArticleData) {

    return getSessionLastMeasurement()
      .then(result => {

        if (!result) {
          // FIXME: We need a new error code for this scenario
          throw new DefaultError('GEN01-00', 'Current session has no measurement');
        }

        const resultData = {
            result: {
              measurementId: result.measurement_id,
              sessionId: result.session_id,
            }
        };

        return this.handleMeasuringResult(resultData, article);
      });

  },

  async handleExistingMeasurement(measurementId: string, article: ArticleData) {

    log.info('[ShopService] handling existing measurement');
    const params = new URLSearchParams(window.location.search);

    // create session if it does notexist
    let sessionId = storageService.getSessionId();

    if (!sessionId && !params.has('fpt-session')) {
      log.warn('[ShopService] could not find sessions id for current measurement');
      sessionId = await this.createSession();
      this.updateSessionId(sessionId);
    } else if (params.has('fpt-session')) {
      sessionId = params.get('fpt-session');
      this.updateSessionId(sessionId);
    }

    const { multiRecommendationsEnabled, articleTags } = shopSettings;

    const tags = RenderUtil.getArticleTagsFromUrlParams() || articleTags;

    if (multiRecommendationsEnabled && tags) {
      recommendationService.setSessionId(sessionId);
      recommendationService.setMeasurementId(measurementId);
      recommendationService.setTags(tags);
      const queryType = shopSettings.tagsQueryType ?
        shopSettings.tagsQueryType : TAGS_QUERY_TYPE.OR;
      recommendationService.setTagsQueryType(queryType);
      return recommendationService.createRecommendation()
        .then(result => result.data);
    }

    const previousMatch = storageService.findMatching(article.number);
    if (previousMatch) {
      previousMatch.isStoredInSession = true;
      return Promise.resolve(previousMatch);
    }

    matchingService.setSessionId(sessionId);
    matchingService.setMeasurementId(measurementId);
    matchingService.setArticleNumber(article.number);

    // create matching
    log.info('Creating matching for existing existing measurement');

    return matchingService.createMatching()
      .then(data => {

        storageService.addMatching(data, article.number);

        return data;
      })
      .catch((err) => {

        if (err instanceof DefaultError) {
          throw err;
        }

        if (err.response && err.response.status == '401') {
          log.warn(`soemething went wrong with getting a match ${err.response.statusText}`);
          log.warn('Seems like Session has expired, creating new session');
          return this.createSession()
            .then(result => this.updateSessionId(result))
            .then(() => {
                return matchingService.createMatching();
            })
            .then(result => {
              const { data } = result;
              storageService.addMatching(data, article.number);
              return data;
            })
            .catch((err) => {
              throw new DefaultError(
                'GEN01-00',
                'Create new session or matching failed',
                { cause: err }
              );
            });
        } else if (
          err.response &&
          err.response.status == '409' &&
          err.response.data &&
          err.response.data.error_code == 'BEDB1-00') {
            throw new DefaultError(
              'BEDB1-00',
              'Article not found',
              { cause: err }
            );
        }

        throw new DefaultError(
          'GEN01-00',
          'Soemething went wrong',
          { cause: err }
        );
      });
  },

  handleAddToCart(matching: Matching, article: ArticleData) {

    if (customShopService) {
      log.info('custom service handling add to cart');
      customShopService.handleMatching(matching);
    } else if (shopSettings.platform === SHOP_PLATFORM.SHOPIFY) {
      log.info('Shopify generic service handling add to cart');
      ShopifyHelper.handleAddToCart(matching, shopSettings);
    } else {
      log.warn('Add to cart called but feature is not supported');
    }

    this.createConversion(matching, article, CONVERSION_ACTION.ADDITEM);
  },

  waitForUserToStartMeasuring() {
    storageService.clearMeasurement();
    storageService.clearMatchings();
    return waitForPollingOutcome(PollingState.MEASURING);
  },

  waitForUserToFinishMeasuring() {
    return waitForPollingOutcome(PollingState.DONE);
  },

  logError(err: DefaultError, article: ArticleData) {

    let cause = 'cause' in err ?
      (err.cause as Error).message : '';

    if (err.errorCode === 'WDAR1' || err.errorCode === 'MADB2-00') {
      cause = article.number;
    }

    clientEventsService.createEvent(
      `shop.error.${(err as DefaultError).errorCode}`,
      cause
    );
  },

  logRecommendationFeedback(recommendation:MultiRecommendation) {

    const gender = [];
    const fits = [];

    if (recommendation.gender) {
      gender.push({
        event_name: "shop.recommendation-feedback",
        event_details: `gender:${recommendation.gender}`,
      });
    }

    const sizeFeedback = recommendation
      .variants.filter(item => item.error_code==='MAST0-00')
      .map(item => {

        if (item.best_fit) {
          fits.push({
            event_name: "shop.recommendation-feedback",
            event_details: `bestFit:${item.article_number}`,
            matching_id: item.matching_id,
          });
        }

        return {
          event_name: "shop.recommendation-feedback",
          event_details: item.size?.feedback,
          matching_id: item.matching_id,
        }

      });

    const clientEvents = gender.concat(fits, sizeFeedback);
    clientEventsService.createEventBatch(clientEvents);
  }
}

export default ShopService;
